import React from 'react';
import { FileText } from 'lucide-react';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Tooltip } from '@cotera/client/app/components/ui/tooltip';
import { cn } from '@cotera/client/v0/lib/utils';
import { useIsBelowMd } from '@cotera/client/app/hooks';
import { useArtifactsIndexVisibility } from './artifacts-index-visibility';

/**
 * Shows and hides the artifacts index card from the chat's own header.
 *
 * Draws nothing until there is something to toggle. That answer comes from the
 * card via {@link useArtifactsIndexVisibility}, and it is false with no
 * artifacts and false while the Canvas is on screen, where the artifact's own
 * bar already carries the switcher. A control that is present but inert in the
 * commonest state teaches people it does nothing.
 *
 * A document glyph, `FileText`. Lucide's rather than the app registry's
 * `document-text`, which is the icon the card's own rows already use for a
 * document artifact — at the registry's 1.5 stroke the header control and a row
 * inside the thing it opens were the same mark at the same size. Lucide's is
 * drawn at stroke 2, matching the `⋯` beside it, and reads as a control rather
 * than as one of the list's entries.
 *
 * It does under-describe: the index also holds charts, datasets, workflows and
 * agents, and a doc glyph names only the commonest of them. A glyph for the
 * whole set reads as no particular thing; a panel glyph names where the card
 * sits rather than what is in it, and collides with the Canvas, which is the
 * right-hand panel this does not open.
 *
 * One glyph across both states, not a pair that swaps. The state is carried by
 * `aria-pressed` and the pressed background, which is how the rest of the app's
 * toggles do it; a glyph that changes has to claim either the current state or
 * the result of pressing, and those read as opposites to different people.
 */
export const ArtifactsIndexToggle: React.FC = () => {
  const visibility = useArtifactsIndexVisibility();
  // Not on a phone. There the panel draws its own `Chat | Artifacts N`
  // switcher across the top of the conversation, which is the same job the card
  // does on a desktop and does it in a row that is always on screen — so the
  // card does not render, and a header control for it would be inert.
  //
  // Literally the same hook the panel reads to pick that layout, rather than a
  // second one that happens to agree today — see {@link useIsBelowMd}.
  const isMobile = useIsBelowMd();

  if (isMobile || visibility === null || !visibility.canShow) {
    return null;
  }

  const label = visibility.isVisible ? 'Hide artifacts' : 'Show artifacts';

  return (
    <Tooltip tooltipContent={label} side="bottom">
      <Button
        variant="ghost"
        size="icon-md"
        aria-label={label}
        aria-pressed={visibility.isVisible}
        onClick={visibility.toggle}
        className={cn(
          'cursor-pointer',
          // Neutral, not the brand tint the composer's plan-mode toggle uses.
          // That control sits alone in the composer's own row; this one sits
          // between Share and the `⋯`, and an indigo chip there was the only
          // colour in the bar — it read as a status, or as the header's one
          // important thing, rather than as a toggle that happens to be on.
          // `bg-accent` is the same grey the ghost buttons settle into when
          // pressed, so "on" looks like the control staying down.
          visibility.isVisible && 'bg-accent text-accent-foreground'
        )}
      >
        {/* `size-4`, the same rem-based step every other glyph in this bar
            uses. It must stay rem-based: the app sets `html { font-size:
            0.8125rem }`, so `size-4` resolves to 13px here and tracks the
            breadcrumb's own `text-sm` if either ever moves. An absolute `18px`
            sat 38% over Share's icon and made the right of the bar read as a
            different, larger design than the trail on the left. */}
        <FileText className="size-4" />
      </Button>
    </Tooltip>
  );
};
